import { ExternalLink } from 'lucide-react'
import {
  stripeDashboardUrl,
  type StripePrice,
  useStripeMeters,
  useStripePrices,
  useStripeProducts,
  useUpdatePricingConfig,
} from '../../api/admin-stripe'

/**
 * Read-only-ish view of Stripe Products / Prices / Meters.
 *
 * The "Active for new orgs" column is the one mutation lever — toggling
 * a price in/out of pricing_config.active_price_ids changes what
 * attach_subscription will pin on freshly-created subscriptions. It does
 * NOT touch existing orgs' subscriptions; for those the operator uses
 * the per-org Subscription Items panel below.
 */
export function StripeInventoryTable() {
  const products = useStripeProducts()
  const prices = useStripePrices()
  const meters = useStripeMeters()
  const update = useUpdatePricingConfig()

  const activeIds = new Set(prices.data?.active_price_ids ?? [])

  function toggleActive(priceId: string) {
    const next = new Set(activeIds)
    if (next.has(priceId)) next.delete(priceId)
    else next.add(priceId)
    update.mutate({ active_price_ids: Array.from(next) })
  }

  const fmtAmount = (p: StripePrice): string => {
    if (p.unit_amount == null) return '—'
    const dollars = p.unit_amount / 100
    return `$${dollars.toFixed(2)} ${(p.currency ?? 'usd').toUpperCase()}`
  }

  return (
    <div className="space-y-6">
      {/* Products */}
      <section className="overflow-hidden rounded-lg border border-border bg-surface">
        <header className="flex items-center justify-between border-b border-border px-4 py-3">
          <div className="font-medium">Products</div>
          <a
            href={stripeDashboardUrl('/products')}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-text-secondary hover:text-primary"
          >
            Open in Stripe <ExternalLink className="h-3 w-3" />
          </a>
        </header>
        {products.isLoading ? (
          <div className="p-6 text-center text-sm text-text-secondary">Loading…</div>
        ) : !products.data || products.data.products.length === 0 ? (
          <div className="p-6 text-center text-sm text-text-secondary">
            No products yet — create one in the Stripe Dashboard.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-elevated text-xs uppercase text-text-secondary">
                <tr>
                  <th className="px-4 py-2 text-left">Name</th>
                  <th className="px-4 py-2 text-left">ID</th>
                  <th className="px-4 py-2 text-right">Prices</th>
                  <th className="px-4 py-2 text-center">Active</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {products.data.products.map((p) => (
                  <tr key={p.id}>
                    <td className="px-4 py-2 font-medium">{p.name}</td>
                    <td className="px-4 py-2 font-mono text-xs">{p.id}</td>
                    <td className="px-4 py-2 text-right">{p.prices.length}</td>
                    <td className="px-4 py-2 text-center">
                      <span className={p.active ? 'text-success' : 'text-text-secondary'}>
                        {p.active ? '●' : '○'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <a
                        href={stripeDashboardUrl(`/products/${p.id}`)}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-text-secondary hover:text-primary"
                      >
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Prices */}
      <section className="overflow-hidden rounded-lg border border-border bg-surface">
        <header className="flex items-center justify-between border-b border-border px-4 py-3">
          <div className="font-medium">Prices</div>
          <a
            href={stripeDashboardUrl('/prices')}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-text-secondary hover:text-primary"
          >
            Open in Stripe <ExternalLink className="h-3 w-3" />
          </a>
        </header>
        {prices.isLoading ? (
          <div className="p-6 text-center text-sm text-text-secondary">Loading…</div>
        ) : !prices.data || prices.data.prices.length === 0 ? (
          <div className="p-6 text-center text-sm text-text-secondary">No prices yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-elevated text-xs uppercase text-text-secondary">
                <tr>
                  <th className="px-4 py-2 text-left">Price ID</th>
                  <th className="px-4 py-2 text-left">Nickname</th>
                  <th className="px-4 py-2 text-left">Type</th>
                  <th className="px-4 py-2 text-right">Amount</th>
                  <th className="px-4 py-2 text-center">Metered</th>
                  <th className="px-4 py-2 text-center">Active for new orgs</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {prices.data.prices.map((p) => (
                  <tr key={p.id}>
                    <td className="px-4 py-2 font-mono text-xs">
                      <a
                        href={stripeDashboardUrl(`/prices/${p.id}`)}
                        target="_blank"
                        rel="noreferrer"
                        className="hover:text-primary"
                      >
                        {p.id}
                      </a>
                    </td>
                    <td className="px-4 py-2">{p.nickname ?? '—'}</td>
                    <td className="px-4 py-2">
                      <span className="text-xs">
                        {p.billing_scheme === 'tiered'
                          ? `tiered/${p.tiers_mode}`
                          : p.recurring_interval ?? p.type ?? '—'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right font-mono text-xs">{fmtAmount(p)}</td>
                    <td className="px-4 py-2 text-center">{p.metered ? '✓' : '—'}</td>
                    <td className="px-4 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={activeIds.has(p.id)}
                        onChange={() => toggleActive(p.id)}
                        disabled={update.isPending}
                        title="Toggle whether attach_subscription pins this price for new orgs"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Meters */}
      <section className="overflow-hidden rounded-lg border border-border bg-surface">
        <header className="flex items-center justify-between border-b border-border px-4 py-3">
          <div className="font-medium">Meters</div>
          <a
            href={stripeDashboardUrl('/billing/meters')}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-text-secondary hover:text-primary"
          >
            Open in Stripe <ExternalLink className="h-3 w-3" />
          </a>
        </header>
        {meters.isLoading ? (
          <div className="p-6 text-center text-sm text-text-secondary">Loading…</div>
        ) : !meters.data || meters.data.meters.length === 0 ? (
          <div className="p-6 text-center text-sm text-text-secondary">No meters yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-elevated text-xs uppercase text-text-secondary">
                <tr>
                  <th className="px-4 py-2 text-left">Display name</th>
                  <th className="px-4 py-2 text-left">event_name</th>
                  <th className="px-4 py-2 text-left">Aggregation</th>
                  <th className="px-4 py-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {meters.data.meters.map((m) => (
                  <tr key={m.id}>
                    <td className="px-4 py-2">{m.display_name ?? '—'}</td>
                    <td className="px-4 py-2 font-mono text-xs">{m.event_name}</td>
                    <td className="px-4 py-2 text-xs">{m.aggregation_formula ?? '—'}</td>
                    <td className="px-4 py-2 text-xs">{m.status ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}
